import type {
  PersonIntegrationEntry,
  PersonIntegrationKey,
  PersonIntegrationSummary,
  WorkspaceAccountStatus,
} from "../types";

const INTEGRATION_LABELS: Record<PersonIntegrationKey, string> = {
  google: "Google Workspace",
  fpt: "FPT",
  okta: "Okta",
  slack: "Slack",
};

type IntegrationCounter = Record<PersonIntegrationKey, { linked: number; notLinked: number }>;

function createEntry(
  key: PersonIntegrationKey,
  overrides: Partial<PersonIntegrationEntry> = {}
): PersonIntegrationEntry {
  return {
    key,
    label: INTEGRATION_LABELS[key],
    state: "not_linked",
    statusLabel: "Not linked",
    ...overrides,
  };
}

function getGoogleWorkspaceStatusLabel(status: WorkspaceAccountStatus | undefined) {
  if (status === "active") return "Linked · Active";
  if (status === "inactive") return "Linked · Inactive";
  return "Not linked";
}

export function buildPersonIntegrationSummary(person: any): PersonIntegrationSummary {
  const googleLinked =
    person?.workspaceAccountStatus === "active" ||
    person?.workspaceAccountStatus === "inactive" ||
    Boolean(person?.googleWorkspaceEmail) ||
    Boolean(person?.googleWorkspaceUserId) ||
    Boolean(person?.isWorkspaceUser);

  const googleDetail = person?.googleWorkspaceEmail || person?.email || undefined;
  const googleLastSyncedAt =
    person?.lastSyncedAt || person?.lastSync || person?.photoCachedAt || undefined;

  const fptLinked = Boolean(person?.fptUserLinked || person?.fptPersonId);
  const fptDetailParts = [person?.identitySource, person?.fptPersonId].filter(Boolean);

  return {
    google: createEntry("google", {
      state: googleLinked ? "linked" : "not_linked",
      statusLabel: getGoogleWorkspaceStatusLabel(person?.workspaceAccountStatus),
      detail: googleLinked
        ? googleDetail || "Linked to this staff record"
        : "No Google Workspace link yet",
      externalEmail: googleDetail,
      externalId: person?.googleWorkspaceUserId || undefined,
      lastSyncedAt: googleLastSyncedAt,
    }),
    fpt: createEntry("fpt", {
      state: fptLinked ? "linked" : "not_linked",
      statusLabel: fptLinked ? "Linked" : "Not linked",
      detail: fptLinked
        ? fptDetailParts.join(" · ") || "Linked to FPT"
        : "No FPT person link yet",
      externalId: person?.fptPersonId || undefined,
      lastSyncedAt: person?.lastSyncedAt || undefined,
    }),
    okta: createEntry("okta", {
      detail: "No Okta link yet",
    }),
    slack: createEntry("slack", {
      detail: "No Slack link yet",
    }),
  };
}

export function summarizePeopleIntegrationCounts(people: any[]): IntegrationCounter {
  return (people || []).reduce<IntegrationCounter>(
    (counts, person) => {
      const summary = buildPersonIntegrationSummary(person);
      (Object.keys(summary) as PersonIntegrationKey[]).forEach((key) => {
        if (summary[key].state === "linked") {
          counts[key].linked += 1;
        } else {
          counts[key].notLinked += 1;
        }
      });
      return counts;
    },
    {
      google: { linked: 0, notLinked: 0 },
      fpt: { linked: 0, notLinked: 0 },
      okta: { linked: 0, notLinked: 0 },
      slack: { linked: 0, notLinked: 0 },
    }
  );
}
